import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaArrowLeft,
  FaSave,
  FaTimes,
  FaYoutube,
  FaClock,
  FaLink,
  FaList,
} from "react-icons/fa";
import axios from "../utils/axiosConfig";
import toast from "react-hot-toast";
import { useFooter } from "../context/FooterContext";

const AdminTopics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setShowFooter } = useFooter();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTopic, setEditingTopic] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [domainId, setDomainId] = useState(null);
  const [domainName, setDomainName] = useState("");
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    order: 0,
    learningOutcomes: "",
    prerequisites: "",
    tags: "",
    estimatedTime: 30,
    difficulty: "beginner",
    primaryVideo: "",
  });

  useEffect(() => {
    setShowFooter(false);
    checkDarkMode();

    const params = new URLSearchParams(location.search);
    const id = params.get("domainId");
    const name = params.get("domainName");

    if (id) {
      setDomainId(id);
      setDomainName(decodeURIComponent(name || ""));
      fetchTopics(id);
    } else {
      toast.error("No domain selected");
      navigate("/admin/domains");
    }

    return () => setShowFooter(true);
  }, [location]);

  const checkDarkMode = () => {
    const dark = document.documentElement.getAttribute("data-theme") === "dark";
    setIsDarkMode(dark);
    const observer = new MutationObserver(() => {
      const dark =
        document.documentElement.getAttribute("data-theme") === "dark";
      setIsDarkMode(dark);
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  };

  const fetchTopics = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get("/api/roadmap/admin/topics", {
        params: { domainId: id },
        headers: { Authorization: `Bearer ${token}` },
      });
      setTopics(response.data.topics);
    } catch (error) {
      console.error("Fetch topics error:", error);
      toast.error("Failed to fetch topics");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.description || !formData.primaryVideo) {
      toast.error("Title, description, and video URL are required");
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const data = {
        title: formData.title,
        description: formData.description,
        domainId: domainId,
        order:
          formData.order === undefined || formData.order === null
            ? 0
            : parseInt(formData.order),
        learningOutcomes: formData.learningOutcomes
          ? formData.learningOutcomes.split("\n").filter((l) => l.trim())
          : [],
        prerequisites: formData.prerequisites
          ? formData.prerequisites.split("\n").filter((p) => p.trim())
          : [],
        tags: formData.tags
          ? formData.tags.split(",").map((t) => t.trim())
          : [],
        estimatedTime:
          formData.estimatedTime === undefined ||
          formData.estimatedTime === null
            ? 30
            : parseInt(formData.estimatedTime),
        difficulty: formData.difficulty,
        primaryVideo: formData.primaryVideo,
      };

      if (editingTopic) {
        await axios.put(`/api/roadmap/admin/topics/${editingTopic._id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Topic updated");
      } else {
        await axios.post("/api/roadmap/admin/topics", data, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Topic created");
      }
      resetForm();
      fetchTopics(domainId);
    } catch (error) {
      console.error("Save topic error:", error);
      toast.error(error.response?.data?.message || "Failed to save topic");
    }
  };

  const handleDelete = async (topic) => {
    if (!window.confirm(`Delete "${topic.title}"?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/api/roadmap/admin/topics/${topic._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Topic deleted");
      fetchTopics(domainId);
    } catch (error) {
      console.error("Delete topic error:", error);
      toast.error("Failed to delete topic");
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      order: topics.length,
      learningOutcomes: "",
      prerequisites: "",
      tags: "",
      estimatedTime: 30,
      difficulty: "beginner",
      primaryVideo: "",
    });
    setEditingTopic(null);
    setShowForm(false);
  };

  const getTextColor = () => (isDarkMode ? "#f1f5f9" : "#1e293b");
  const getTextSecondary = () => (isDarkMode ? "#94a3b8" : "#64748b");
  const getBorderColor = () =>
    isDarkMode ? "rgba(139,92,246,0.2)" : "rgba(139,92,246,0.1)";
  const getCardBackground = () =>
    isDarkMode ? "rgba(30, 30, 45, 0.8)" : "rgba(255, 255, 255, 0.9)";
  const getInputBackground = () => (isDarkMode ? "rgba(0,0,0,0.3)" : "#ffffff");

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "60vh",
        }}
      >
        <FaSpinner
          style={{
            animation: "spin 1s linear infinite",
            fontSize: "2rem",
            color: "#8b5cf6",
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: "1200px",
        margin: "0 auto",
        padding: "2rem",
        background: isDarkMode ? "#0a0a0f" : "#f5f7fb",
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "2rem",
          flexWrap: "wrap",
          gap: "1rem",
        }}
      >
        <div>
          <button
            onClick={() => navigate("/admin/domains")}
            style={{
              background: "none",
              border: "none",
              color: "#8b5cf6",
              cursor: "pointer",
              marginBottom: "0.5rem",
              display: "flex",
              alignItems: "center",
              gap: "0.3rem",
            }}
          >
            <FaArrowLeft /> Back to Domains
          </button>
          <h1 style={{ color: getTextColor(), fontSize: "1.8rem", margin: 0 }}>
            Manage Topics: {domainName}
          </h1>
          <p style={{ color: getTextSecondary(), marginTop: "0.3rem" }}>
            Create and manage topics/lessons for this domain
          </p>
        </div>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            style={{
              padding: "0.8rem 1.5rem",
              background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              fontWeight: "500",
            }}
          >
            <FaPlus /> Add Topic
          </button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            background: getCardBackground(),
            borderRadius: "20px",
            padding: "1.5rem",
            marginBottom: "2rem",
            border: `1px solid ${getBorderColor()}`,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
            }}
          >
            <h2 style={{ color: getTextColor(), margin: 0 }}>
              {editingTopic ? "Edit Topic" : "Add New Topic"}
            </h2>
            <button
              onClick={resetForm}
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                color: getTextSecondary(),
              }}
            >
              <FaTimes size={20} />
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Topic Title *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
                placeholder="e.g., Introduction to MongoDB"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="What students will learn in this topic"
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                required
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                <FaYoutube
                  style={{ marginRight: "0.3rem", color: "#ff0000" }}
                />
                YouTube Video URL *
              </label>
              <input
                type="text"
                value={formData.primaryVideo}
                onChange={(e) =>
                  setFormData({ ...formData, primaryVideo: e.target.value })
                }
                placeholder="https://www.youtube.com/watch?v=VIDEO_ID"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
                required
              />
              <p
                style={{
                  fontSize: "0.7rem",
                  color: getTextSecondary(),
                  marginTop: "0.3rem",
                }}
              >
                Supports YouTube links (regular or shortened)
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
                marginBottom: "1rem",
              }}
            >
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextColor(),
                  }}
                >
                  Difficulty
                </label>
                <select
                  value={formData.difficulty}
                  onChange={(e) =>
                    setFormData({ ...formData, difficulty: e.target.value })
                  }
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                >
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "0.5rem",
                    color: getTextColor(),
                  }}
                >
                  <FaClock style={{ marginRight: "0.3rem" }} />
                  Estimated Time (minutes)
                </label>
                <input
                  type="number"
                  value={
                    formData.estimatedTime === undefined ||
                    formData.estimatedTime === null
                      ? 30
                      : formData.estimatedTime
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      estimatedTime:
                        e.target.value === "" ? 30 : parseInt(e.target.value),
                    })
                  }
                  placeholder="30"
                  style={{
                    width: "100%",
                    padding: "0.8rem",
                    border: `2px solid ${getBorderColor()}`,
                    borderRadius: "10px",
                    background: getInputBackground(),
                    color: getTextColor(),
                  }}
                />
              </div>
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                <FaList style={{ marginRight: "0.3rem" }} />
                Learning Outcomes (one per line)
              </label>
              <textarea
                value={formData.learningOutcomes}
                onChange={(e) =>
                  setFormData({ ...formData, learningOutcomes: e.target.value })
                }
                placeholder="Understand MongoDB basics&#10;Learn CRUD operations&#10;Master aggregation pipeline"
                rows="3"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                <FaLink style={{ marginRight: "0.3rem" }} />
                Prerequisites (one per line)
              </label>
              <textarea
                value={formData.prerequisites}
                onChange={(e) =>
                  setFormData({ ...formData, prerequisites: e.target.value })
                }
                placeholder="Basic JavaScript knowledge&#10;ES6 syntax"
                rows="2"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Tags (comma separated)
              </label>
              <input
                type="text"
                value={formData.tags}
                onChange={(e) =>
                  setFormData({ ...formData, tags: e.target.value })
                }
                placeholder="mongodb, database, nosql"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div style={{ marginBottom: "1rem" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "0.5rem",
                  color: getTextColor(),
                }}
              >
                Order
              </label>
              <input
                type="number"
                value={
                  formData.order === undefined || formData.order === null
                    ? 0
                    : formData.order
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    order: e.target.value === "" ? 0 : parseInt(e.target.value),
                  })
                }
                placeholder="Display order (0 = first)"
                style={{
                  width: "100%",
                  padding: "0.8rem",
                  border: `2px solid ${getBorderColor()}`,
                  borderRadius: "10px",
                  background: getInputBackground(),
                  color: getTextColor(),
                }}
              />
            </div>
            <div style={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
              <button
                type="button"
                onClick={resetForm}
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="submit"
                style={{
                  flex: 1,
                  padding: "0.8rem",
                  background: "linear-gradient(135deg, #8b5cf6, #3b82f6)",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                <FaSave /> {editingTopic ? "Update" : "Create"} Topic
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Topics List */}
      <div style={{ display: "grid", gap: "1rem" }}>
        {topics.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "3rem",
              background: getCardBackground(),
              borderRadius: "20px",
              border: `1px solid ${getBorderColor()}`,
            }}
          >
            <FaYoutube size={48} color={getTextSecondary()} />
            <p style={{ color: getTextSecondary(), marginTop: "1rem" }}>
              No topics created yet in this domain.
            </p>
            <button
              onClick={() => setShowForm(true)}
              style={{
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                background: "#8b5cf6",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: "pointer",
              }}
            >
              <FaPlus /> Create your first topic
            </button>
          </div>
        ) : (
          topics.map((topic, index) => (
            <motion.div
              key={topic._id}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              style={{
                background: getCardBackground(),
                borderRadius: "16px",
                padding: "1rem",
                border: `1px solid ${getBorderColor()}`,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  gap: "1rem",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      flexWrap: "wrap",
                      marginBottom: "0.3rem",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.7rem",
                        color: "#8b5cf6",
                        background: "#8b5cf620",
                        padding: "0.2rem 0.5rem",
                        borderRadius: "4px",
                      }}
                    >
                      #{index + 1}
                    </span>
                    <h3 style={{ color: getTextColor(), margin: 0 }}>
                      {topic.title}
                    </h3>
                    <span
                      style={{
                        fontSize: "0.7rem",
                        padding: "0.2rem 0.5rem",
                        borderRadius: "4px",
                        background:
                          topic.difficulty === "beginner"
                            ? "#10b98120"
                            : topic.difficulty === "intermediate"
                              ? "#f59e0b20"
                              : "#ef444420",
                        color:
                          topic.difficulty === "beginner"
                            ? "#10b981"
                            : topic.difficulty === "intermediate"
                              ? "#f59e0b"
                              : "#ef4444",
                      }}
                    >
                      {topic.difficulty}
                    </span>
                    <span
                      style={{ fontSize: "0.7rem", color: getTextSecondary() }}
                    >
                      <FaClock
                        style={{ marginRight: "0.2rem", display: "inline" }}
                      />
                      {topic.estimatedTime} min
                    </span>
                  </div>
                  <p
                    style={{
                      color: getTextSecondary(),
                      fontSize: "0.8rem",
                      marginBottom: "0.3rem",
                    }}
                  >
                    {topic.description?.substring(0, 120)}...
                  </p>
                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      flexWrap: "wrap",
                      fontSize: "0.7rem",
                      color: getTextSecondary(),
                    }}
                  >
                    <span>🎬 {topic.primaryVideo?.substring(0, 50)}...</span>
                    <span>📚 {topic.resources?.length || 0} resources</span>
                    <span>🏷️ {topic.tags?.join(", ") || "No tags"}</span>
                  </div>
                  {topic.learningOutcomes?.length > 0 && (
                    <div style={{ marginTop: "0.5rem" }}>
                      <span
                        style={{
                          fontSize: "0.65rem",
                          color: getTextSecondary(),
                        }}
                      >
                        📖 {topic.learningOutcomes.length} learning outcomes
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                  <button
                    onClick={() => {
                      setEditingTopic(topic);
                      setFormData({
                        title: topic.title,
                        description: topic.description,
                        order: topic.order,
                        learningOutcomes:
                          topic.learningOutcomes?.join("\n") || "",
                        prerequisites: topic.prerequisites?.join("\n") || "",
                        tags: topic.tags?.join(", ") || "",
                        estimatedTime: topic.estimatedTime,
                        difficulty: topic.difficulty,
                        primaryVideo: topic.primaryVideo,
                      });
                      setShowForm(true);
                    }}
                    style={{
                      padding: "0.4rem 0.8rem",
                      background: "#ffc107",
                      color: "#333",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(topic)}
                    style={{
                      padding: "0.4rem 0.8rem",
                      background: "#dc3545",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                    }}
                  >
                    <FaTrash /> Delete
                  </button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminTopics;
